import { Vec3 } from '@/core/math/Vec3';
import { clamp } from '@/core/math/scalar';

export interface AeroConfig {
  /** Frontal reference area with the nose into the airflow, m^2. */
  frontalArea: number;
  /** Reference area broadside to the airflow, m^2. */
  broadsideArea: number;
  /** Drag coefficient at zero angle of attack. */
  dragCoefficientAxial: number;
  /** Drag coefficient broadside. */
  dragCoefficientBroadside: number;
  /** Wing area for lift, m^2. Zero for an unwinged capsule. */
  wingArea: number;
  /** Lift-curve slope, per radian. */
  liftSlope: number;
  /** Angle of attack at which the wing stalls, radians. */
  stallAngle: number;
  /**
   * Distance from the centre of mass to the centre of pressure along the body
   * +Z axis, metres. Positive means the centre of pressure is *behind* the
   * centre of mass, which makes the vessel weathervane into the airflow.
   */
  staticMargin: number;
  /** Reference length for the pitch damping term, metres. */
  referenceLength: number;
  /** Effectiveness multiplier for control surfaces, m^2 per radian. */
  controlSurfaceArea: number;
}

export interface AeroResult {
  /** Aerodynamic force in the reference frame, N. */
  force: Vec3;
  /** Aerodynamic torque in the reference frame, N m. */
  torque: Vec3;
  /** Dynamic pressure, Pa. */
  dynamicPressure: number;
  /** Angle between the nose and the airflow, radians. */
  angleOfAttack: number;
  /** Airspeed relative to the local air mass, m/s. */
  airspeed: number;
  /** Mach number, or 0 when the speed of sound is undefined. */
  mach: number;
  /** Convective heating rate at the stagnation point, W/m^2. */
  heatFlux: number;
}

const EMPTY: AeroResult = {
  force: new Vec3(),
  torque: new Vec3(),
  dynamicPressure: 0,
  angleOfAttack: 0,
  airspeed: 0,
  mach: 0,
  heatFlux: 0,
};

/**
 * Sutton-Graves style stagnation-point convective heating constant.
 * Tuned for gameplay rather than for a specific gas mixture, but it preserves
 * the physically important scaling: heating goes as sqrt(density) * velocity^3.
 */
const HEATING_CONSTANT = 1.83e-4;

/**
 * Aerodynamic force and torque model.
 *
 * This is deliberately not "vacuum flight plus a screen effect". The force
 * depends on the air density, the square of the speed relative to the moving
 * air mass, the vehicle's orientation relative to that airflow, and the wing
 * geometry. A vessel entering an atmosphere sideways experiences a large
 * broadside drag and a restoring torque that swings the nose (or the heat
 * shield) into the flow, exactly as it should.
 */
export function computeAerodynamics(
  config: AeroConfig,
  relativeAirVelocity: Vec3,
  density: number,
  orientation: { rotate(v: Vec3, out?: Vec3): Vec3; rotateInverse(v: Vec3, out?: Vec3): Vec3 },
  angularVelocity: Vec3,
  speedOfSound: number,
  controlDeflection: Vec3,
): AeroResult {
  const airspeed = relativeAirVelocity.length();
  if (density <= 0 || airspeed < 1e-3) {
    return { ...EMPTY, force: new Vec3(), torque: new Vec3() };
  }

  const q = 0.5 * density * airspeed * airspeed;
  const flowDir = relativeAirVelocity.clone().mulScalar(1 / airspeed);

  // Airflow direction expressed in body coordinates. Body +Z is the nose, and
  // `flowDir` is the direction the vessel is travelling relative to the air, so
  // the angle of attack is the angle between the nose and the direction of
  // travel: zero means flying nose-first.
  //
  // The sign here is load-bearing. Inverting it makes the centre-of-pressure
  // moment anti-restoring, so instead of weathervaning into the airflow a
  // vessel is driven away from it and tumbles until the integrator diverges.
  const flowBody = orientation.rotateInverse(flowDir);
  const alpha = Math.acos(clamp(flowBody.z, -1, 1));

  // --- Drag ---------------------------------------------------------------
  // Blend between axial and broadside characteristics using sin^2(alpha),
  // which is the standard Newtonian-flow interpolation and behaves correctly at
  // both extremes.
  const s2 = Math.sin(alpha) ** 2;
  const cd = config.dragCoefficientAxial + (config.dragCoefficientBroadside - config.dragCoefficientAxial) * s2;
  const area = config.frontalArea + (config.broadsideArea - config.frontalArea) * s2;
  const dragMagnitude = q * cd * area;
  const force = flowDir.clone().mulScalar(-dragMagnitude);

  // --- Lift ---------------------------------------------------------------
  let liftMagnitude = 0;
  if (config.wingArea > 0 && alpha > 1e-4) {
    // Effective angle of attack measured from the nose-forward attitude.
    const effAlpha = Math.PI - alpha > alpha ? alpha : Math.PI - alpha;
    let cl = config.liftSlope * effAlpha;
    if (effAlpha > config.stallAngle) {
      // Post-stall: lift collapses toward a flat-plate value rather than
      // continuing to grow, so pulling too hard actually loses the wing.
      const over = (effAlpha - config.stallAngle) / Math.max(1e-3, Math.PI / 2 - config.stallAngle);
      cl = config.liftSlope * config.stallAngle * Math.max(0.15, 1 - over * 1.35);
    }
    liftMagnitude = q * cl * config.wingArea;

    // Lift acts perpendicular to the airflow, in the plane containing the
    // airflow and the vessel's dorsal axis.
    const upBody = new Vec3(0, 1, 0);
    const upWorld = orientation.rotate(upBody);
    let liftDir = upWorld.rejectFromUnit(flowDir);
    if (liftDir.lengthSq() < 1e-12) {
      liftDir = Vec3.anyPerpendicular(flowDir);
    }
    liftDir.normalize();
    force.addScaled(liftDir, liftMagnitude);
  }

  // --- Restoring (weathervane) torque -------------------------------------
  const torque = new Vec3();
  if (config.staticMargin !== 0) {
    // The aerodynamic force effectively acts at the centre of pressure, which
    // sits `staticMargin` behind the centre of mass along -Z in body space.
    const copBody = new Vec3(0, 0, -config.staticMargin);
    const copWorld = orientation.rotate(copBody);
    // Only the component perpendicular to the body axis generates a useful
    // restoring moment; the axial part is already handled by the drag force.
    const perpForce = force.rejectFromUnit(orientation.rotate(new Vec3(0, 0, 1)).normalize());
    torque.add(Vec3.crossVectors(copWorld, perpForce));
  }

  // --- Aerodynamic damping ------------------------------------------------
  // Rotating in an airstream is resisted. Without this term a vessel oscillates
  // around prograde forever instead of settling.
  const dampCoefficient = q * config.broadsideArea * config.referenceLength * 0.02;
  torque.addScaled(angularVelocity, -dampCoefficient);

  // --- Control surfaces ---------------------------------------------------
  if (config.controlSurfaceArea > 0 && controlDeflection.lengthSq() > 0) {
    const authority = q * config.controlSurfaceArea * config.referenceLength;
    const bodyTorque = new Vec3(
      controlDeflection.x * authority,
      controlDeflection.y * authority,
      controlDeflection.z * authority * 0.45,
    );
    torque.add(orientation.rotate(bodyTorque));
  }

  const mach = speedOfSound > 0 ? airspeed / speedOfSound : 0;
  const heatFlux = HEATING_CONSTANT * Math.sqrt(density) * airspeed ** 3;

  return {
    force,
    torque,
    dynamicPressure: q,
    angleOfAttack: alpha,
    airspeed,
    mach,
    heatFlux,
  };
}
